/**
 * useConversationRoot — resolves the TRUE root ancestor of a conversation by
 * walking up the parent_id chain, bounded to UI_MAX_SUBAGENT_DEPTH levels
 * (ENG-4404 / former ENG-4513).
 *
 * AIAssistant aggregates HITL interactions across the WHOLE conversation tree.
 * The descendant discovery (useDescendantConversations) finds everything BELOW
 * an anchor, so it must be anchored on the tree ROOT — not the immediate parent
 * — or requests on the root or a sibling branch are dropped when the operator
 * is viewing a deep descendant (P -> C1 -> G2: viewing G2 must anchor on P).
 *
 * Mechanism (mirrors useDescendantConversations): a render-time upward walk
 * over rows already known (the selected conversation + each settled ancestor
 * probe read from the query cache) drives a dual-partition useQueries fan-out
 * of exact-conversation lookups. An ancestor may live in EITHER partition, so
 * each needed ancestor gets a user AND a tenant probe. BFS converges one level
 * per round trip.
 *
 * Progressive widening: rootId starts at the immediate parent (parity with the
 * former subagentAnchorId) and climbs monotonically toward the true root as
 * ancestors resolve — the id-set only ever widens, never flashes narrower.
 *
 * Termination: parent_id === 'self' (root sentinel, conversation.go) or empty
 * → current node is the root. Depth bound = UI_MAX_SUBAGENT_DEPTH; if the chain
 * is longer or an ancestor fails to resolve, rootId falls back to the highest
 * ancestor resolved so far (fail-safe, never below the immediate parent).
 */
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { FALLBACK_POLL_MS } from '@/hooks/useChat';
import { UI_MAX_SUBAGENT_DEPTH } from '@/hooks/useDescendantConversations';
import { getQueryKey, useUserKeySuffix } from '@/hooks/useQueryKeys';
import { Conversation } from '@/types';

type Partition = 'user' | 'tenant';

function isRootEdge(parent: string | undefined): boolean {
  return !parent || parent === 'self';
}

function ancestorKey(
  id: string,
  partition: Partition,
  userSuffix: string
): readonly unknown[] {
  return [
    ...getQueryKey.getConversationAncestor(id, partition),
    ...(userSuffix ? [userSuffix] : []),
  ];
}

export interface ConversationRoot {
  /** Highest ancestor resolved so far. Undefined only when conversationId is
   *  undefined. Starts at the immediate parent, climbs to the true root. */
  rootId?: string;
  isResolving: boolean;
}

export function useConversationRoot(
  conversationId?: string,
  selected?: Conversation
): ConversationRoot {
  const axios = useAxios();
  const queryClient = useQueryClient();
  const userSuffix = useUserKeySuffix();

  // Known conversation rows: the selected conversation plus any ancestor row
  // already in the query cache. Keyed by uuid||id.
  const known = new Map<string, Conversation>();
  const addKnown = (c?: Conversation) => {
    const id = c?.uuid || c?.id;
    if (id && !known.has(id)) known.set(id, c!);
  };
  addKnown(selected);

  // Upward walk: from the selected conversation, climb parent_id while the
  // parent row is known. The FIRST parent id we cannot resolve becomes the
  // fetch frontier (one ancestor per round trip). rootId is the highest id we
  // can name — the immediate parent at minimum, the true root once resolved.
  let rootId: string | undefined;
  let needFetchId: string | undefined;
  if (conversationId) {
    let cur: Conversation | undefined = selected;
    let curId = conversationId;
    for (let depth = 0; depth < UI_MAX_SUBAGENT_DEPTH; depth++) {
      const parentId: string | undefined = cur?.parent_id;
      if (!cur || isRootEdge(parentId)) {
        rootId = curId; // curId is the root (or we cannot climb further)
        break;
      }
      // parentId is a valid ancestor id — adopt it as the best-known root now
      // (progressive widening), then try to climb into its row.
      rootId = parentId;
      const parentRow =
        known.get(parentId!) ??
        // Read the settled probe from cache (either partition).
        (['user', 'tenant'] as const)
          .map(p =>
            queryClient.getQueryData<Conversation[]>(
              ancestorKey(parentId!, p, userSuffix)
            )
          )
          .flatMap(rows => rows ?? [])
          .find(r => (r.uuid || r.id) === parentId);
      if (!parentRow) {
        needFetchId = parentId; // fetch it, climb further next round
        break;
      }
      addKnown(parentRow);
      cur = parentRow;
      curId = parentId!;
    }
  }

  // Dual-partition exact-conversation fetch for the one frontier ancestor.
  const fetchKey = needFetchId ?? '';
  const queryConfigs = useMemo(() => {
    if (!fetchKey) return [];
    return (['user', 'tenant'] as const).map(partition => ({
      queryKey: ancestorKey(fetchKey, partition, userSuffix),
      queryFn: async () => {
        // Same /my contract + param allowlist as useDescendantConversations
        // (label/key/user only — no widening toggles). The exact-conversation
        // key is `#conversation#<id>` — the proven shape useGetConversationById
        // sends over the wire (useMy composes `#${id}` into this; the backend
        // matches it BeginsWith on the key index — conversation.go / query).
        const response = await axios.get('/my', {
          params: {
            label: 'conversation',
            key: `#conversation#${fetchKey}`,
            ...(partition === 'user' ? { user: 'true' } : {}),
          },
        });
        return (response.data?.conversations ?? []) as Conversation[];
      },
      retry: false,
      refetchInterval: FALLBACK_POLL_MS,
      enabled: !!fetchKey,
    }));
  }, [fetchKey, userSuffix, axios]);

  const fetches = useQueries({ queries: queryConfigs });
  // Read dataUpdatedAt on every result to subscribe to settle (tracked-props
  // pattern, mirrors useDescendantConversations). isResolving tracks in-flight
  // fetches ONLY: once the frontier ancestor's probes settle (even empty — a
  // broken/unresolvable chain), we stop resolving and fall back to the highest
  // ancestor named so far (fail-safe). needFetchId is deliberately NOT part of
  // isResolving — a settled-but-empty probe leaves needFetchId set, and gating
  // on it would spin forever.
  const anyPending = fetches.some(f => f.isLoading);
  const settleStamp = fetches.reduce((a, f) => a + f.dataUpdatedAt, 0);

  return useMemo(
    () => ({ rootId, isResolving: anyPending }),
    // settleStamp forces re-eval when an ancestor probe lands.

    [rootId, anyPending, settleStamp]
  );
}
